import apiClient from './index.js';

// ---- Public share endpoints (no auth token) ----
// These are recipient-facing endpoints that do not require Cognito auth.
// The backend derives identity from shareId after verification.

/**
 * Load share metadata by share ID.
 * GET /share/v1/{shareId}
 */
export function getShare(shareId) {
  return apiClient.get(`/share/v1/${encodeURIComponent(shareId)}`, null);
}

/**
 * Request a verification code be sent to the recipient's email.
 * POST /share/v1/verify  { id, type: 'request' }
 */
export function requestVerificationCode(shareId) {
  return apiClient.post('/share/v1/verify', null, { id: shareId, type: 'request' });
}

/**
 * Validate a verification code.
 * POST /share/v1/verify  { id, type: 'validate', code }
 */
export function validateVerificationCode(shareId, code) {
  return apiClient.post('/share/v1/verify', null, { id: shareId, type: 'validate', code });
}

/**
 * Request a download for a verified share.
 * SS-370 B2: NEVER send userId/username — backend derives identity from shareId.
 *
 * @param {Object} params
 * @param {string} params.distributionId
 * @param {string} params.email
 * @param {string} params.shareId
 * @param {Array}  [params.setInfo]
 */
export function requestDownload({ distributionId, email, shareId, setInfo }) {
  const payload = { distributionId, email, shareId };
  if (setInfo && Array.isArray(setInfo) && setInfo.length > 0) {
    payload.setInfo = setInfo;
  }
  return apiClient.post('/share/v1/request-download', null, payload);
}

/**
 * Fetch media thumbnails for a verified share.
 * GET /share/v1/{shareId}/media
 * Returns presigned S3 URLs for collection thumbnails/optimized.
 */
export function getShareMedia(shareId) {
  return apiClient.get(`/share/v1/${encodeURIComponent(shareId)}/media`, null);
}

/**
 * Poll for asynchronous download status.
 * GET /share/v1/download-status?taskId={taskId}
 * SS-448: Caller must pass the `ownerToken` returned from `requestDownload` (202 body).
 * The token is sent via the `X-Download-Token` header. Keep the token in memory only.
 */
export function getDownloadStatus(taskId, ownerToken) {
  const headers = ownerToken ? { 'X-Download-Token': ownerToken } : {};
  return apiClient.get(
    `/share/v1/download-status?taskId=${encodeURIComponent(taskId)}`,
    null,
    { headers }
  );
}

/**
 * Map share API response status to a safe, user-facing message.
 * Never leaks backend error details to the user.
 *
 * @param {number|null} status  HTTP status code
 * @param {Object|null} [body]  Optional parsed response body
 * @returns {string} Safe user-facing error message
 */
export function shareErrorMessage(status, _body) {
  if (status === 410) return 'This secure link has expired. Please request a new link.';
  if (status === 403) return 'We could not verify access for this secure link.';
  if (status === 404) return 'This secure link could not be found.';
  if (status === 400) return 'This share is no longer available.';
  if (status === 401) return 'Please log in to access this share.';
  if (status >= 500) return 'This share could not be loaded right now. Please try again later.';
  return 'Unable to complete this share action.';
}

// ---- Authenticated share endpoints (require Cognito token) ----

export class SharesApi {
  /**
   * Get shares for a distribution (by distributionId)
   */
  static async getByDistribution(token, distributionId) {
    return apiClient.get(`/share/v1/distribution/${distributionId}/shares`, token);
  }

  /**
   * Get usage dashboard data for a distribution.
   * GET /share/v1/distribution/{distributionId}/usage
   *
   * @param {string} token - Cognito auth token
   * @param {string} distributionId
   * @param {Object} [params] - Query filters: from, to, shareId, email, eventType, limit, cursor
   */
  static async getDistributionUsage(token, distributionId, params = {}) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value != null && value !== '') query.set(key, String(value));
    }
    const qs = query.toString();
    const url = `/share/v1/distribution/${distributionId}/usage${qs ? `?${qs}` : ''}`;
    return apiClient.get(url, token);
  }

  /**
   * Redrive a failed download task.
   * POST /share/v1/download-tasks/{taskId}/redrive
   *
   * @param {string} token - Cognito auth token
   * @param {string} taskId
   */
  static async redriveDownload(token, taskId) {
    return apiClient.post(`/share/v1/download-tasks/${taskId}/redrive`, token, {});
  }
}

export default SharesApi;
